import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';

// ── Animation ────────────────────────────────────────────

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ── Layout ───────────────────────────────────────────────

export const Container = styled.article`
  height: 100%;
`;

// ── Page Header ──────────────────────────────────────────

export const PageHeader = styled.header`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 0.75rem;

    h1 {
      font-size: 1.375rem;
      font-weight: 800;
      color: ${theme.colors.foreground[100]};
      letter-spacing: -0.5px;
    }
  `}
`;

export const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const BackButton = styled.button`
  ${({ theme }) => css`
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.75rem;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${theme.colors.brand.primary.default};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: ${theme.transition.default};

    :hover {
      background: ${theme.colors.brand.primary.opaque};
    }
  `}
`;

export const CountBadge = styled.span`
  ${({ theme }) => css`
    border-radius: 9999px;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.25rem 0.75rem;
  `}
`;

// ── Category Section ─────────────────────────────────────

export const CategorySection = styled.section`
  margin-bottom: 1.75rem;
`;

type CategoryHeaderProps = { $color?: string | null };

export const CategoryHeader = styled.div<CategoryHeaderProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.875rem;
  margin-left: 2.5rem;

  ${down('sm')} {
    margin-left: 0;
  }
`;

export const CategoryIcon = styled.span<CategoryHeaderProps>`
  font-size: 0.8125rem;
  opacity: 0.6;
  color: ${({ theme, $color }) => $color || theme.colors.brand.primary.default};
`;

export const CategoryName = styled.span<CategoryHeaderProps>`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${({ theme, $color }) => $color || theme.colors.brand.primary.default};
`;

export const CategoryLine = styled.div<CategoryHeaderProps>`
  flex: 1;
  height: 1px;
  margin-left: 0.25rem;
  background: ${({ theme, $color }) => {
    const color = $color || theme.colors.brand.primary.default;
    return `linear-gradient(90deg, ${color}18, transparent)`;
  }};
`;

// ── Sponsor Grid ─────────────────────────────────────────

type SponsorGridProps = { $columns?: number };

export const SponsorGrid = styled.div<SponsorGridProps>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns || 3}, 1fr);
  gap: 1rem;
  margin-left: 2.5rem;

  ${down('lg')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${down('sm')} {
    grid-template-columns: 1fr;
    margin-left: 0;
  }
`;

// ── Sponsor Card ─────────────────────────────────────────

type SponsorCardProps = { $delay?: number };

export const SponsorCard = styled.div<SponsorCardProps>`
  ${({ theme, $delay = 0 }) => css`
    background: ${theme.colors.background[80]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    height: 8.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: hidden;
    position: relative;
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    animation: ${fadeUp} 0.4s ${Math.min($delay, 8) * 0.04}s ease both;

    &:hover {
      transform: translateY(-4px);
      border-color: ${theme.colors.foreground[40]};
      box-shadow: ${theme.shadows.md};
    }
  `}
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  width: 100%;
  height: 100%;

  > img {
    max-width: 100%;
    max-height: 3.5rem;
    object-fit: contain;
    filter: grayscale(10%);
    opacity: 0.9;
    transition: all 0.3s ease;
  }

  ${SponsorCard}:hover > & > img {
    filter: grayscale(0%);
    opacity: 1;
  }
`;

// ── Skeleton ─────────────────────────────────────────────

export const SkeletonCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    height: 8.125rem;
    overflow: hidden;
  `}
`;
